import { useState, useEffect, Fragment } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { toCsv } from '../../utils/csv.js';

// ── Date helpers (UTC-safe ISO yyyy-mm-dd) ─────────────────────────────────────
const iso = (d) => d.toISOString().slice(0, 10);
const todayISO = () => iso(new Date());
const addDaysISO = (n) => iso(new Date(Date.now() + n * 86400000));
function monthStartISO(offset = 0) {
  const d = new Date();
  return iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1)));
}
function monthEndISO(offset = 0) {
  const d = new Date();
  return iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset + 1, 0)));
}
function quarterStartISO() {
  const d = new Date();
  const qm = Math.floor(d.getUTCMonth() / 3) * 3;
  return iso(new Date(Date.UTC(d.getUTCFullYear(), qm, 1)));
}
function mondayISO() {
  const d = new Date();
  const dow = d.getUTCDay();
  return addDaysISO(-(dow === 0 ? 6 : dow - 1));
}

const fmtH = (v) => {
  const n = parseFloat(v) || 0;
  return n === 0 ? '—' : (n % 1 === 0 ? `${n}h` : `${n.toFixed(1)}h`);
};
const num = (v) => parseFloat(v) || 0;

function downloadCsv(filename, header, rows) {
  const csv = toCsv(header, rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

const PRESETS = [
  { key: 'week',   label: 'This week',  range: () => [mondayISO(), todayISO()] },
  { key: 'month',  label: 'This month', range: () => [monthStartISO(0), todayISO()] },
  { key: 'last',   label: 'Last month', range: () => [monthStartISO(-1), monthEndISO(-1)] },
  { key: 'quarter',label: 'This quarter', range: () => [quarterStartISO(), todayISO()] },
  { key: '30',     label: 'Last 30 days', range: () => [addDaysISO(-30), todayISO()] },
  { key: 'all',    label: 'All time', range: () => ['2000-01-01', todayISO()] },
];

const TABS = [
  { key: 'person',   label: 'By Person' },
  { key: 'team',     label: 'By Team' },
  { key: 'sow',      label: 'By SOW' },
  { key: 'task',     label: 'By Task' },
  { key: 'overhead', label: 'By Overhead' },
];

// Editable entry-level grid for one SOW. Everyone with analysis access can view
// the entries; admin/HR/PMO (server-confirmed via can_edit) can edit hours/date/
// milestone, delete, or add entries.
function SowEntriesEditor({ sowId, from, to, onChanged }) {
  const [data, setData]       = useState(null);   // { can_edit, entries }
  const [milestones, setMs]   = useState([]);
  const [people, setPeople]   = useState([]);
  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({});
  const [adding, setAdding]   = useState(false);
  const [addForm, setAddForm] = useState({});
  const [busy, setBusy]       = useState(false);

  const load = () => api.get(`hours-analysis/sow/${sowId}/entries`, { from, to })
    .then(d => setData(d)).catch(() => setData({ can_edit: false, entries: [] }));
  useEffect(() => { load(); }, [sowId, from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get('milestones', { sow_id: sowId }).then(m => setMs(m || [])).catch(() => {}); }, [sowId]);

  const canEdit = !!data?.can_edit;
  const ensurePeople = () => { if (!people.length) api.get('people').then(p => setPeople(p || [])).catch(() => {}); };
  const after = async () => { await load(); onChanged?.(); };

  const startEdit = (e) => { setAdding(false); setEditId(e.id); setEditForm({ hours: String(e.hours), date: e.date, milestone_id: e.milestone_id || '' }); };
  const saveEdit = async (e) => {
    const h = parseFloat(editForm.hours);
    if (!h || h <= 0) { alert('Enter valid hours'); return; }
    setBusy(true);
    try { await api.put(`time-entries/${e.id}`, { hours: h, date: editForm.date, milestone_id: editForm.milestone_id || null }); setEditId(null); await after(); }
    catch (err) { alert(err.message || 'Save failed'); }
    setBusy(false);
  };
  const del = async (e) => {
    if (!window.confirm(`Delete ${e.person_name}'s ${e.hours}h on ${e.date}?`)) return;
    setBusy(true);
    try { await api.delete(`time-entries/${e.id}`); await after(); }
    catch (err) { alert(err.message || 'Delete failed'); }
    setBusy(false);
  };
  const openAdd = () => { ensurePeople(); setEditId(null); setAdding(true); setAddForm({ person_id: '', date: to, milestone_id: '', hours: '1' }); };
  const saveAdd = async () => {
    const h = parseFloat(addForm.hours);
    if (!addForm.person_id || !h || h <= 0) { alert('Pick a person and enter valid hours'); return; }
    setBusy(true);
    try {
      await api.post('time-entries', { person_id: addForm.person_id, sow_id: sowId, milestone_id: addForm.milestone_id || null, date: addForm.date, hours: h, source: 'hours-analysis' });
      setAdding(false); await after();
    } catch (err) { alert(err.message || 'Add failed'); }
    setBusy(false);
  };

  if (!data) return <div className="text-[9px] text-gray-400 py-2">Loading entries…</div>;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[8px] uppercase font-bold text-gray-400">Entries ({data.entries.length})</div>
        {canEdit && !adding && (
          <button onClick={openAdd} className="text-[9px] font-semibold text-navy hover:underline">+ Add entry</button>
        )}
      </div>
      <table className="w-full">
        <thead>
          <tr className="text-[8px] uppercase text-gray-400 font-bold border-b border-gray-200">
            <th className="px-2 py-1 text-left">Person</th>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Milestone</th>
            <th className="px-2 py-1 text-left">Band</th>
            <th className="px-2 py-1 text-right">Hours</th>
            <th className="px-2 py-1 text-right">Cost</th>
            {canEdit && <th className="px-2 py-1" />}
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="bg-emerald-50/60 border-b border-gray-100">
              <td className="px-2 py-1">
                <select className="form-input text-[9px] py-0.5" value={addForm.person_id} onChange={e => setAddForm(f => ({ ...f, person_id: e.target.value }))}>
                  <option value="">Select person…</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </td>
              <td className="px-2 py-1"><input type="date" className="form-input text-[9px] py-0.5" value={addForm.date} onChange={e => setAddForm(f => ({ ...f, date: e.target.value }))} /></td>
              <td className="px-2 py-1">
                <select className="form-input text-[9px] py-0.5" value={addForm.milestone_id} onChange={e => setAddForm(f => ({ ...f, milestone_id: e.target.value }))}>
                  <option value="">—</option>
                  {milestones.map(m => <option key={m.id} value={m.id}>{m.milestone_code}</option>)}
                </select>
              </td>
              <td className="px-2 py-1 text-[9px] text-gray-400">auto</td>
              <td className="px-2 py-1"><input type="number" step="0.25" min="0.25" className="form-input text-[9px] py-0.5 text-right w-16" value={addForm.hours} onChange={e => setAddForm(f => ({ ...f, hours: e.target.value }))} /></td>
              <td />
              <td className="px-2 py-1 text-right whitespace-nowrap">
                <button onClick={saveAdd} disabled={busy} className="text-[9px] font-semibold text-emerald-600 hover:underline mr-2">Save</button>
                <button onClick={() => setAdding(false)} className="text-[9px] text-gray-400 hover:underline">Cancel</button>
              </td>
            </tr>
          )}
          {data.entries.map(e => editId === e.id ? (
            <tr key={e.id} className="bg-blue-50/60 border-b border-gray-100">
              <td className="px-2 py-1 text-[10px] text-gray-700">{e.person_name}</td>
              <td className="px-2 py-1"><input type="date" className="form-input text-[9px] py-0.5" value={editForm.date} onChange={ev => setEditForm(f => ({ ...f, date: ev.target.value }))} /></td>
              <td className="px-2 py-1">
                <select className="form-input text-[9px] py-0.5" value={editForm.milestone_id} onChange={ev => setEditForm(f => ({ ...f, milestone_id: ev.target.value }))}>
                  <option value="">—</option>
                  {milestones.map(m => <option key={m.id} value={m.id}>{m.milestone_code}</option>)}
                </select>
              </td>
              <td className="px-2 py-1 text-[10px] text-gray-500">{e.band}</td>
              <td className="px-2 py-1"><input type="number" step="0.25" min="0.25" className="form-input text-[9px] py-0.5 text-right w-16" value={editForm.hours} onChange={ev => setEditForm(f => ({ ...f, hours: ev.target.value }))} /></td>
              <td />
              <td className="px-2 py-1 text-right whitespace-nowrap">
                <button onClick={() => saveEdit(e)} disabled={busy} className="text-[9px] font-semibold text-blue-600 hover:underline mr-2">Save</button>
                <button onClick={() => setEditId(null)} className="text-[9px] text-gray-400 hover:underline">Cancel</button>
              </td>
            </tr>
          ) : (
            <tr key={e.id} className="border-b border-gray-100">
              <td className="px-2 py-1 text-[10px] text-gray-700">{e.person_name}</td>
              <td className="px-2 py-1 text-[10px] text-gray-600">{e.date}</td>
              <td className="px-2 py-1 text-[10px] text-gray-600">{e.milestone_code}</td>
              <td className="px-2 py-1 text-[10px] text-gray-600">{e.band}</td>
              <td className="px-2 py-1 text-[10px] text-right font-medium">{fmtH(e.hours)}</td>
              <td className="px-2 py-1 text-[10px] text-right text-rose-600">{formatMoney(num(e.cost))}</td>
              {canEdit && (
                <td className="px-2 py-1 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(e)} className="text-[9px] text-blue-500 hover:underline mr-2">Edit</button>
                  <button onClick={() => del(e)} className="text-[9px] text-red-400 hover:underline">Del</button>
                </td>
              )}
            </tr>
          ))}
          {!data.entries.length && !adding && (
            <tr><td colSpan={canEdit ? 7 : 6} className="px-2 py-2 text-[9px] text-gray-400 text-center">No entries.</td></tr>
          )}
        </tbody>
      </table>
      {!canEdit && <div className="text-[8px] text-gray-400 mt-1">View only — admin/HR/PMO can edit, delete or add.</div>}
    </div>
  );
}

// Editable entry-level grid for one overhead category (mirrors SowEntriesEditor,
// minus milestones — overhead time isn't booked against a milestone).
function OverheadEntriesEditor({ categoryId, from, to, onChanged }) {
  const [data, setData]       = useState(null);   // { can_edit, entries }
  const [people, setPeople]   = useState([]);
  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({});
  const [adding, setAdding]   = useState(false);
  const [addForm, setAddForm] = useState({});
  const [busy, setBusy]       = useState(false);

  const load = () => api.get(`hours-analysis/overhead/${categoryId}/entries`, { from, to })
    .then(d => setData(d)).catch(() => setData({ can_edit: false, entries: [] }));
  useEffect(() => { load(); }, [categoryId, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEdit = !!data?.can_edit;
  const ensurePeople = () => { if (!people.length) api.get('people').then(p => setPeople(p || [])).catch(() => {}); };
  const after = async () => { await load(); onChanged?.(); };

  const startEdit = (e) => { setAdding(false); setEditId(e.id); setEditForm({ hours: String(e.hours), date: e.date }); };
  const saveEdit = async (e) => {
    const h = parseFloat(editForm.hours);
    if (!h || h <= 0) { alert('Enter valid hours'); return; }
    setBusy(true);
    try { await api.put(`time-entries/${e.id}`, { hours: h, date: editForm.date }); setEditId(null); await after(); }
    catch (err) { alert(err.message || 'Save failed'); }
    setBusy(false);
  };
  const del = async (e) => {
    if (!window.confirm(`Delete ${e.person_name}'s ${e.hours}h on ${e.date}?`)) return;
    setBusy(true);
    try { await api.delete(`time-entries/${e.id}`); await after(); }
    catch (err) { alert(err.message || 'Delete failed'); }
    setBusy(false);
  };
  const openAdd = () => { ensurePeople(); setEditId(null); setAdding(true); setAddForm({ person_id: '', date: to, hours: '1' }); };
  const saveAdd = async () => {
    const h = parseFloat(addForm.hours);
    if (!addForm.person_id || !h || h <= 0) { alert('Pick a person and enter valid hours'); return; }
    setBusy(true);
    try {
      await api.post('time-entries', { person_id: addForm.person_id, overhead_category_id: categoryId === 'none' ? null : categoryId, date: addForm.date, hours: h, source: 'hours-analysis' });
      setAdding(false); await after();
    } catch (err) { alert(err.message || 'Add failed'); }
    setBusy(false);
  };

  if (!data) return <div className="text-[9px] text-gray-400 py-2">Loading entries…</div>;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[8px] uppercase font-bold text-gray-400">Entries ({data.entries.length})</div>
        {canEdit && !adding && (
          <button onClick={openAdd} className="text-[9px] font-semibold text-navy hover:underline">+ Add entry</button>
        )}
      </div>
      <table className="w-full">
        <thead>
          <tr className="text-[8px] uppercase text-gray-400 font-bold border-b border-gray-200">
            <th className="px-2 py-1 text-left">Person</th>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Band</th>
            <th className="px-2 py-1 text-right">Hours</th>
            <th className="px-2 py-1 text-right">Cost</th>
            {canEdit && <th className="px-2 py-1" />}
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="bg-emerald-50/60 border-b border-gray-100">
              <td className="px-2 py-1">
                <select className="form-input text-[9px] py-0.5" value={addForm.person_id} onChange={e => setAddForm(f => ({ ...f, person_id: e.target.value }))}>
                  <option value="">Select person…</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </td>
              <td className="px-2 py-1"><input type="date" className="form-input text-[9px] py-0.5" value={addForm.date} onChange={e => setAddForm(f => ({ ...f, date: e.target.value }))} /></td>
              <td className="px-2 py-1 text-[9px] text-gray-400">auto</td>
              <td className="px-2 py-1"><input type="number" step="0.25" min="0.25" className="form-input text-[9px] py-0.5 text-right w-16" value={addForm.hours} onChange={e => setAddForm(f => ({ ...f, hours: e.target.value }))} /></td>
              <td />
              <td className="px-2 py-1 text-right whitespace-nowrap">
                <button onClick={saveAdd} disabled={busy} className="text-[9px] font-semibold text-emerald-600 hover:underline mr-2">Save</button>
                <button onClick={() => setAdding(false)} className="text-[9px] text-gray-400 hover:underline">Cancel</button>
              </td>
            </tr>
          )}
          {data.entries.map(e => editId === e.id ? (
            <tr key={e.id} className="bg-blue-50/60 border-b border-gray-100">
              <td className="px-2 py-1 text-[10px] text-gray-700">{e.person_name}</td>
              <td className="px-2 py-1"><input type="date" className="form-input text-[9px] py-0.5" value={editForm.date} onChange={ev => setEditForm(f => ({ ...f, date: ev.target.value }))} /></td>
              <td className="px-2 py-1 text-[10px] text-gray-500">{e.band}</td>
              <td className="px-2 py-1"><input type="number" step="0.25" min="0.25" className="form-input text-[9px] py-0.5 text-right w-16" value={editForm.hours} onChange={ev => setEditForm(f => ({ ...f, hours: ev.target.value }))} /></td>
              <td />
              <td className="px-2 py-1 text-right whitespace-nowrap">
                <button onClick={() => saveEdit(e)} disabled={busy} className="text-[9px] font-semibold text-blue-600 hover:underline mr-2">Save</button>
                <button onClick={() => setEditId(null)} className="text-[9px] text-gray-400 hover:underline">Cancel</button>
              </td>
            </tr>
          ) : (
            <tr key={e.id} className="border-b border-gray-100">
              <td className="px-2 py-1 text-[10px] text-gray-700">{e.person_name}</td>
              <td className="px-2 py-1 text-[10px] text-gray-600">{e.date}</td>
              <td className="px-2 py-1 text-[10px] text-gray-600">{e.band}</td>
              <td className="px-2 py-1 text-[10px] text-right font-medium">{fmtH(e.hours)}</td>
              <td className="px-2 py-1 text-[10px] text-right text-rose-600">{formatMoney(num(e.cost))}</td>
              {canEdit && (
                <td className="px-2 py-1 text-right whitespace-nowrap">
                  {/* Written from approved leave: the leave request is what
                      changes it, so it is not edited or deleted here. */}
                  {e.leave_request_id ? (
                    <span className="text-[9px] text-gray-400" title="From approved leave. Change or cancel the leave request and this follows it.">From leave</span>
                  ) : (<>
                    <button onClick={() => startEdit(e)} className="text-[9px] text-blue-500 hover:underline mr-2">Edit</button>
                    <button onClick={() => del(e)} className="text-[9px] text-red-400 hover:underline">Del</button>
                  </>)}
                </td>
              )}
            </tr>
          ))}
          {!data.entries.length && !adding && (
            <tr><td colSpan={canEdit ? 6 : 5} className="px-2 py-2 text-[9px] text-gray-400 text-center">No entries.</td></tr>
          )}
        </tbody>
      </table>
      {!canEdit && <div className="text-[8px] text-gray-400 mt-1">View only — admin/HR/PMO can edit, delete or add.</div>}
    </div>
  );
}

export default function HoursAnalysisPage() {
  const [from, setFrom]   = useState(monthStartISO(0));
  const [to, setTo]       = useState(todayISO());
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]     = useState('person');
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded]   = useState(null);   // sow_id whose drill-down is open
  const [details, setDetails]     = useState({});     // sow_id → breakdown rows
  const [detailBusy, setDetailBusy] = useState(false);
  const [expandedOH, setExpandedOH]   = useState(null);   // category_id whose drill-down is open
  const [detailsOH, setDetailsOH]     = useState({});     // category_id → breakdown rows
  const [detailBusyOH, setDetailBusyOH] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setExpanded(null); setDetails({});       // ranges changed — drop cached drill-downs
    setExpandedOH(null); setDetailsOH({});
    api.get('hours-analysis', { from, to })
      .then(d => { if (live) { setData(d); setLoading(false); } })
      .catch(() => { if (live) { setData(null); setLoading(false); } });
    return () => { live = false; };
  }, [from, to]);

  const toggleSow = async (sowId) => {
    if (expanded === sowId) { setExpanded(null); return; }
    setExpanded(sowId);
    if (!details[sowId]) {
      setDetailBusy(true);
      try {
        const d = await api.get(`hours-analysis/sow/${sowId}`, { from, to });
        setDetails(prev => ({ ...prev, [sowId]: d.rows || [] }));
      } catch { setDetails(prev => ({ ...prev, [sowId]: [] })); }
      setDetailBusy(false);
    }
  };

  // After an edit/add/delete: refresh the top-level totals/lists and this SOW's roll-up.
  const refreshSow = async (sowId) => {
    try {
      const [main, det] = await Promise.all([
        api.get('hours-analysis', { from, to }),
        api.get(`hours-analysis/sow/${sowId}`, { from, to }),
      ]);
      setData(main);
      setDetails(prev => ({ ...prev, [sowId]: det.rows || [] }));
    } catch { /* noop */ }
  };

  const toggleOverhead = async (categoryId) => {
    if (expandedOH === categoryId) { setExpandedOH(null); return; }
    setExpandedOH(categoryId);
    if (!detailsOH[categoryId]) {
      setDetailBusyOH(true);
      try {
        const d = await api.get(`hours-analysis/overhead/${categoryId}`, { from, to });
        setDetailsOH(prev => ({ ...prev, [categoryId]: d.rows || [] }));
      } catch { setDetailsOH(prev => ({ ...prev, [categoryId]: [] })); }
      setDetailBusyOH(false);
    }
  };

  // After an edit/add/delete: refresh the top-level totals/lists and this category's roll-up.
  const refreshOverhead = async (categoryId) => {
    try {
      const [main, det] = await Promise.all([
        api.get('hours-analysis', { from, to }),
        api.get(`hours-analysis/overhead/${categoryId}`, { from, to }),
      ]);
      setData(main);
      setDetailsOH(prev => ({ ...prev, [categoryId]: det.rows || [] }));
    } catch { /* noop */ }
  };

  const applyPreset = (p) => { const [f, t] = p.range(); setFrom(f); setTo(t); };

  const t = data?.totals || {};

  // Column config + rows for the active tab.
  const view = (() => {
    if (!data) return { cols: [], rows: [] };
    switch (tab) {
      case 'person': return {
        cols: [
          { h: 'Person', get: r => `${r.person_name}` },
          { h: 'Team', get: r => r.team_name },
          { h: 'Task', get: r => fmtH(r.task_hours), align: 'right' },
          { h: 'SOW', get: r => fmtH(r.sow_hours), align: 'right' },
          { h: 'Overhead', get: r => fmtH(r.overhead_hours), align: 'right' },
          { h: 'Total', get: r => fmtH(r.total), align: 'right', bold: true },
        ],
        rows: data.by_person,
        csv: { header: ['Person', 'Team', 'Task (h)', 'SOW (h)', 'Overhead (h)', 'Total (h)'],
               row: r => [r.person_name, r.team_name, num(r.task_hours), num(r.sow_hours), num(r.overhead_hours), num(r.total)] },
      };
      case 'team': return {
        cols: [
          { h: 'Team', get: r => r.team_name },
          { h: 'People', get: r => r.people, align: 'right' },
          { h: 'Task', get: r => fmtH(r.task_hours), align: 'right' },
          { h: 'SOW', get: r => fmtH(r.sow_hours), align: 'right' },
          { h: 'Overhead', get: r => fmtH(r.overhead_hours), align: 'right' },
          { h: 'Total', get: r => fmtH(r.total), align: 'right', bold: true },
        ],
        rows: data.by_team,
        csv: { header: ['Team', 'People', 'Task (h)', 'SOW (h)', 'Overhead (h)', 'Total (h)'],
               row: r => [r.team_name, r.people, num(r.task_hours), num(r.sow_hours), num(r.overhead_hours), num(r.total)] },
      };
      case 'sow': return {
        rows: data.by_sow,
        csv: { header: ['SOW', 'Client', 'Total (h)', 'Cost (£)'],
               row: r => [r.reference, r.client_name || '', num(r.total), Math.round(num(r.total_cost))] },
      };
      case 'task': return {
        cols: [
          { h: 'Task', get: r => r.task_name || '—' },
          { h: 'SOW', get: r => r.sow_reference || '—' },
          { h: 'Total', get: r => fmtH(r.total), align: 'right', bold: true },
        ],
        rows: data.by_task,
        csv: { header: ['Task', 'SOW', 'Total (h)'], row: r => [r.task_name || '', r.sow_reference || '', num(r.total)] },
      };
      case 'overhead': return {
        cols: [
          { h: 'Overhead category', get: r => r.category },
          { h: 'Total', get: r => fmtH(r.total), align: 'right', bold: true },
        ],
        rows: data.by_overhead,
        csv: { header: ['Overhead category', 'Total (h)'], row: r => [r.category, num(r.total)] },
      };
      default: return { cols: [], rows: [] };
    }
  })();

  const exportCurrent = () => {
    if (!view.rows?.length) return;
    downloadCsv(`hours-by-${tab}_${from}_to_${to}.csv`, view.csv.header, view.rows.map(view.csv.row));
  };

  const exportRaw = async () => {
    setExporting(true);
    try {
      const d = await api.get('hours-analysis/export', { from, to });
      const header = ['Date', 'Person', 'Team', 'Type', 'SOW', 'Task', 'Overhead', 'Hours', 'Billable', 'Notes'];
      const rows = (d.rows || []).map(r => [
        r.date, r.person_name, r.team_name || '', r.type, r.sow_reference || '',
        r.task_name || '', r.overhead_category || '', num(r.hours), r.billable ? 'Yes' : 'No', r.description || '',
      ]);
      downloadCsv(`hours-entries_${from}_to_${to}.csv`, header, rows);
    } catch { /* noop */ }
    setExporting(false);
  };

  return (
    <div className="fade-in p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-base font-extrabold text-navy">Hours Analysis</h1>
          <div className="text-[9px] text-gray-500 mt-0.5">Logged hours sliced by person, team, SOW, task and overhead — across any date range.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCurrent} disabled={loading || !view.rows?.length}
            className="px-2 py-1 text-[9px] border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-40 font-semibold">⬇ Export this view</button>
          <button onClick={exportRaw} disabled={loading || exporting}
            className="px-2 py-1 text-[9px] bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40 font-semibold">
            {exporting ? 'Preparing…' : '⬇ Export all entries'}</button>
        </div>
      </div>

      {/* Date controls */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => applyPreset(p)}
              className="px-2 py-1 text-[9px] border border-gray-200 rounded bg-white hover:bg-gray-50 font-semibold text-gray-600">{p.label}</button>
          ))}
        </div>
        <span className="text-gray-300">·</span>
        <label className="text-[9px] text-gray-500 flex items-center gap-1">From
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} className="form-input text-[9px] py-0.5" /></label>
        <label className="text-[9px] text-gray-500 flex items-center gap-1">To
          <input type="date" value={to} min={from} max={todayISO()} onChange={e => setTo(e.target.value)} className="form-input text-[9px] py-0.5" /></label>
        {loading && <span className="text-[9px] text-gray-400">Loading…</span>}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-7 gap-2 mb-4">
        {[
          { label: 'Total', value: t.total, color: 'text-navy' },
          { label: 'Labour cost', value: t.total_cost, color: 'text-rose-600', money: true },
          { label: 'Billable', value: t.billable_hours, color: 'text-emerald-600' },
          { label: 'Task', value: t.task_hours, color: 'text-blue-600' },
          { label: 'SOW', value: t.sow_hours, color: 'text-teal-600' },
          { label: 'Overhead', value: t.overhead_hours, color: 'text-amber-600' },
          { label: 'People', value: t.people, color: 'text-gray-700', raw: true },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-100 rounded-lg px-3 py-2 text-center">
            <div className={`text-[16px] font-black ${s.color}`}>{s.money ? formatMoney(s.value || 0) : s.raw ? (s.value || 0) : fmtH(s.value)}</div>
            <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wide mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Group-by tabs */}
      <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit mb-3">
        {TABS.map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            className={`px-3 py-1.5 text-[10px] font-bold border-r last:border-r-0 transition-colors ${tab === tb.key ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'sow' && (
        <div className="text-[8px] text-gray-400 mb-1">Click a SOW to drill into resource × milestone × band, with cost.</div>
      )}
      {tab === 'overhead' && (
        <div className="text-[8px] text-gray-400 mb-1">Click a category to drill into who logged it, by resource × band, with cost.</div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        {loading ? (
          <div className="text-center py-12 text-[10px] text-gray-400">Loading…</div>
        ) : tab === 'sow' ? (
          !data?.by_sow?.length ? (
            <div className="text-center py-12 text-[10px] text-gray-400">No SOW hours in this period.</div>
          ) : (
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold">SOW</th>
                  <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold">Client</th>
                  <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold">Hours</th>
                  <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold">Labour cost</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.by_sow.map(r => {
                  const open = expanded === r.sow_id;
                  const drill = details[r.sow_id];
                  return (
                    <Fragment key={r.sow_id}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => toggleSow(r.sow_id)}>
                        <td className="px-3 py-2 text-[10px] font-semibold text-navy">{r.reference}</td>
                        <td className="px-3 py-2 text-[10px] text-gray-600">{r.client_name || '—'}</td>
                        <td className="px-3 py-2 text-[10px] text-right font-bold text-navy">{fmtH(r.total)}</td>
                        <td className="px-3 py-2 text-[10px] text-right text-rose-600">{formatMoney(num(r.total_cost))}</td>
                        <td className="px-2 py-2 text-[10px] text-gray-400 text-center">{open ? '▾' : '▸'}</td>
                      </tr>
                      {open && (
                        <tr className="bg-gray-50/60">
                          <td colSpan={5} className="px-3 py-2">
                            {/* Roll-up summary: resource × milestone × band */}
                            {!drill ? (
                              <div className="text-[9px] text-gray-400 py-2">{detailBusy ? 'Loading…' : '—'}</div>
                            ) : drill.length > 0 && (
                              <table className="w-full mb-3">
                                <thead>
                                  <tr className="text-[8px] uppercase text-gray-400 font-bold">
                                    <th className="px-2 py-1 text-left">Resource</th>
                                    <th className="px-2 py-1 text-left">Milestone</th>
                                    <th className="px-2 py-1 text-left">Band</th>
                                    <th className="px-2 py-1 text-right">Hours</th>
                                    <th className="px-2 py-1 text-right">Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {drill.map((d, i) => (
                                    <tr key={i} className="border-t border-gray-100">
                                      <td className="px-2 py-1 text-[10px] text-gray-700">{d.person_name}</td>
                                      <td className="px-2 py-1 text-[10px] text-gray-600">{d.milestone_code}{d.milestone_desc ? ` · ${d.milestone_desc}` : ''}</td>
                                      <td className="px-2 py-1 text-[10px] text-gray-600">{d.band}</td>
                                      <td className="px-2 py-1 text-[10px] text-right font-medium">{fmtH(d.hours)}</td>
                                      <td className="px-2 py-1 text-[10px] text-right text-rose-600">{formatMoney(num(d.cost))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {/* Editable line-by-line entries */}
                            <SowEntriesEditor sowId={r.sow_id} from={from} to={to} onChanged={() => refreshSow(r.sow_id)} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )
        ) : tab === 'overhead' ? (
          !data?.by_overhead?.length ? (
            <div className="text-center py-12 text-[10px] text-gray-400">No overhead hours in this period.</div>
          ) : (
            <table className="w-full min-w-[420px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold">Overhead category</th>
                  <th className="px-3 py-2 text-right text-[8px] uppercase text-gray-500 font-bold">Total</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.by_overhead.map(r => {
                  const open = expandedOH === r.category_id;
                  const drill = detailsOH[r.category_id];
                  return (
                    <Fragment key={r.category_id}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => toggleOverhead(r.category_id)}>
                        <td className="px-3 py-2 text-[10px] font-semibold text-navy">{r.category}</td>
                        <td className="px-3 py-2 text-[10px] text-right font-bold text-navy">{fmtH(r.total)}</td>
                        <td className="px-2 py-2 text-[10px] text-gray-400 text-center">{open ? '▾' : '▸'}</td>
                      </tr>
                      {open && (
                        <tr className="bg-gray-50/60">
                          <td colSpan={3} className="px-3 py-2">
                            {/* Roll-up summary: resource × band */}
                            {!drill ? (
                              <div className="text-[9px] text-gray-400 py-2">{detailBusyOH ? 'Loading…' : '—'}</div>
                            ) : drill.length > 0 && (
                              <table className="w-full mb-3">
                                <thead>
                                  <tr className="text-[8px] uppercase text-gray-400 font-bold">
                                    <th className="px-2 py-1 text-left">Resource</th>
                                    <th className="px-2 py-1 text-left">Band</th>
                                    <th className="px-2 py-1 text-right">Hours</th>
                                    <th className="px-2 py-1 text-right">Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {drill.map((d, i) => (
                                    <tr key={i} className="border-t border-gray-100">
                                      <td className="px-2 py-1 text-[10px] text-gray-700">{d.person_name}</td>
                                      <td className="px-2 py-1 text-[10px] text-gray-600">{d.band}</td>
                                      <td className="px-2 py-1 text-[10px] text-right font-medium">{fmtH(d.hours)}</td>
                                      <td className="px-2 py-1 text-[10px] text-right text-rose-600">{formatMoney(num(d.cost))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {/* Editable line-by-line entries */}
                            <OverheadEntriesEditor categoryId={r.category_id} from={from} to={to} onChanged={() => refreshOverhead(r.category_id)} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )
        ) : !view.rows?.length ? (
          <div className="text-center py-12 text-[10px] text-gray-400">No hours logged in this period.</div>
        ) : (
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {view.cols.map(c => (
                  <th key={c.h} className={`px-3 py-2 text-[8px] uppercase text-gray-500 font-bold ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {view.rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  {view.cols.map(c => (
                    <td key={c.h} className={`px-3 py-2 text-[10px] ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.bold ? 'font-bold text-navy' : 'text-gray-700'}`}>
                      {c.get(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-3 text-[8px] text-gray-400 leading-relaxed">
        Hours come from logged time entries. <strong>Task</strong> = time against a project task, <strong>SOW</strong> = time booked direct to a SOW, <strong>Overhead</strong> = non-project time. SOW totals include task time rolled up to the parent SOW.
        <strong> Cost</strong> = hours × the rate snapshotted on each entry (band day-rate ÷ 7.5) — the same basis as Profitability; entries with no rate count as £0, so run the Profitability page's rate back-fill if costs look low.
        These logged hours are the <strong>actuals</strong> — once a resource forecast exists they can be compared against it. "Export all entries" includes milestone, band, day-rate and cost per line for pivoting in Excel.
      </div>
    </div>
  );
}
